import { supabase } from '@/integrations/supabase/client';

export interface RecurringEventPattern {
  id: string;
  event_id: string;
  pattern_type: 'daily' | 'weekly' | 'monthly' | 'yearly';
  pattern_interval: number;
  pattern_days?: number[]; // For weekly patterns
  end_date?: string;
  max_occurrences?: number;
  default_guests: Array<{
    email?: string;
    phone?: string;
    name?: string;
  }>;
  guest_inheritance_type: 'all_instances' | 'per_instance';
  auto_generate_instances: boolean;
  next_generation_date?: string;
  event?: {
    id: string;
    name: string;
    description?: string;
    rsvp_date?: string;
    rsvp_time?: string;
    rsvp_location?: string;
  };
}

export interface EventInstance {
  id: string;
  name: string;
  description?: string;
  parent_event_id: string;
  instance_date: string;
  occurrence_number: number;
  rsvp_time?: string;
  rsvp_location?: string;
  is_active: boolean;
  guest_count?: number;
}

export const recurringEventService = {
  async getRecurringEvents(): Promise<RecurringEventPattern[]> {
    const { data, error } = await supabase
      .from('recurring_events')
      .select(`
        *,
        event:events(id, name, description, rsvp_date, rsvp_time, rsvp_location)
      `)
      .order('created_at', { ascending: false });

    if (error) {
      console.error('Error fetching recurring events:', error);
      throw error;
    }

    return (data || []).map(item => ({
      ...item,
      pattern_type: item.pattern_type as 'daily' | 'weekly' | 'monthly' | 'yearly',
      guest_inheritance_type: item.guest_inheritance_type as 'all_instances' | 'per_instance',
      default_guests: typeof item.default_guests === 'string' 
        ? JSON.parse(item.default_guests) 
        : Array.isArray(item.default_guests) 
        ? item.default_guests 
        : [],
      pattern_days: item.pattern_days ? (typeof item.pattern_days === 'string' ? JSON.parse(item.pattern_days) : item.pattern_days) : undefined,
    }));
  },

  async createRecurringEvent(eventData: {
    name: string;
    description?: string;
    event_type: 'voting' | 'rsvp';
    rsvp_date?: string;
    rsvp_time?: string;
    rsvp_location?: string;
    pattern_type: 'daily' | 'weekly' | 'monthly' | 'yearly';
    pattern_interval: number;
    pattern_days?: number[];
    end_date?: string;
    max_occurrences?: number;
    default_guests: Array<{
      email?: string;
      phone?: string;
      name?: string;
    }>;
    guest_inheritance_type: 'all_instances' | 'per_instance';
    family_group_id?: string;
    client_id?: string;
  }): Promise<RecurringEventPattern> {
    const { data: { user } } = await supabase.auth.getUser();
    if (!user) throw new Error('User not authenticated');

    // First, create the base event
    const { data: event, error: eventError } = await supabase
      .from('events')
      .insert({
        user_id: user.id,
        name: eventData.name,
        description: eventData.description,
        host_name: user.user_metadata?.display_name || user.email?.split('@')[0] || 'Host',
        event_type: eventData.event_type,
        rsvp_date: eventData.rsvp_date,
        rsvp_time: eventData.rsvp_time,
        rsvp_location: eventData.rsvp_location,
        is_recurring: true,
        family_group_id: eventData.family_group_id,
        client_id: eventData.client_id,
      })
      .select()
      .single();

    if (eventError || !event) {
      console.error('Error creating base event:', eventError);
      throw eventError;
    }

    // Then create the recurring pattern
    const { data: recurringEvent, error: recurringError } = await supabase
      .from('recurring_events')
      .insert({
        event_id: event.id,
        pattern_type: eventData.pattern_type,
        pattern_interval: eventData.pattern_interval,
        pattern_days: eventData.pattern_days ? JSON.stringify(eventData.pattern_days) : null,
        end_date: eventData.end_date,
        max_occurrences: eventData.max_occurrences,
        default_guests: JSON.stringify(eventData.default_guests),
        guest_inheritance_type: eventData.guest_inheritance_type,
        auto_generate_instances: true,
      })
      .select()
      .single();

    if (recurringError) {
      // Clean up the base event if recurring creation fails
      await supabase.from('events').delete().eq('id', event.id);
      console.error('Error creating recurring pattern:', recurringError);
      throw recurringError;
    }

    // Add default guests to the base event if specified
    if (eventData.default_guests.length > 0) {
      const guestInserts = eventData.default_guests.map(guest => ({
        event_id: event.id,
        email: guest.email,
        phone: guest.phone,
        name: guest.name,
      }));

      const { error: guestsError } = await supabase
        .from('event_guests')
        .insert(guestInserts);

      if (guestsError) {
        console.error('Error adding guests to base event:', guestsError);
      }
    }

    // Generate initial instances
    await this.generateInstances(recurringEvent.id);

    return { 
      ...recurringEvent, 
      event,
      pattern_type: recurringEvent.pattern_type as 'daily' | 'weekly' | 'monthly' | 'yearly',
      guest_inheritance_type: recurringEvent.guest_inheritance_type as 'all_instances' | 'per_instance',
      default_guests: eventData.default_guests,
      pattern_days: eventData.pattern_days,
    };
  },

  async generateInstances(recurringEventId: string): Promise<number> {
    const { data, error } = await supabase.rpc('generate_recurring_event_instances', {
      recurring_event_id: recurringEventId,
    });

    if (error) {
      console.error('Error generating instances:', error);
      throw error;
    }

    return data || 0;
  },

  async getEventInstances(parentEventId: string): Promise<EventInstance[]> {
    const { data, error } = await supabase
      .from('events')
      .select(`
        id,
        name,
        description,
        parent_event_id,
        instance_date,
        occurrence_number,
        rsvp_time,
        rsvp_location,
        is_active,
        event_guests(count)
      `)
      .eq('parent_event_id', parentEventId)
      .order('instance_date');

    if (error) {
      console.error('Error fetching event instances:', error);
      throw error;
    }

    return (data || []).map(instance => ({
      ...instance,
      guest_count: instance.event_guests?.[0]?.count || 0,
    }));
  },

  async deleteRecurringEvent(recurringEventId: string): Promise<void> {
    // Get the recurring event first
    const { data: recurringEvent, error: fetchError } = await supabase
      .from('recurring_events')
      .select('event_id')
      .eq('id', recurringEventId)
      .single();

    if (fetchError) {
      console.error('Error fetching recurring event:', fetchError);
      throw fetchError;
    }

    // Delete all instances first (they reference the parent event)
    const { error: instancesError } = await supabase
      .from('events')
      .delete()
      .eq('parent_event_id', recurringEvent.event_id);

    if (instancesError) {
      console.error('Error deleting event instances:', instancesError);
      throw instancesError;
    }

    // Delete the recurring pattern
    const { error: recurringError } = await supabase
      .from('recurring_events')
      .delete()
      .eq('id', recurringEventId);

    if (recurringError) {
      console.error('Error deleting recurring pattern:', recurringError);
      throw recurringError;
    }

    // Finally delete the base event
    const { error: eventError } = await supabase
      .from('events')
      .delete()
      .eq('id', recurringEvent.event_id);

    if (eventError) {
      console.error('Error deleting base event:', eventError);
      throw eventError;
    }
  },

  async updateRecurringEvent(
    recurringEventId: string,
    updates: Partial<RecurringEventPattern>
  ): Promise<void> {
    const { error } = await supabase
      .from('recurring_events')
      .update({
        pattern_type: updates.pattern_type,
        pattern_interval: updates.pattern_interval,
        pattern_days: updates.pattern_days ? JSON.stringify(updates.pattern_days) : null,
        end_date: updates.end_date,
        max_occurrences: updates.max_occurrences,
        default_guests: updates.default_guests ? JSON.stringify(updates.default_guests) : null,
        guest_inheritance_type: updates.guest_inheritance_type,
      })
      .eq('id', recurringEventId);

    if (error) {
      console.error('Error updating recurring event:', error);
      throw error;
    }
  },

  async toggleInstanceStatus(instanceId: string, isActive: boolean): Promise<void> {
    const { error } = await supabase
      .from('events')
      .update({ is_active: isActive })
      .eq('id', instanceId);

    if (error) {
      console.error('Error toggling instance status:', error);
      throw error;
    }
  },
};